// Search modal — live-filter over the active deck. Opened from the
// footer ⌕ button. Typing filters cards whose faces contain the
// normalized query as a substring; clicking a result jumps the
// session straight to that card. Complements the tag-axis filter
// (which narrows by class/domain/register) with a bottom-up lookup.

import { LANG_SHORT } from "../config/langs";
import type { CardList } from "../config/lists";
import { normalizeAnswer } from "../domain/text";
import { t } from "../i18n";
import type { Card, Direction } from "../types";
import { byId, hide, show } from "./dom";
import { escapeHtml } from "./html";

export type SearchDeps = {
  getCards: () => Card[];
  getDirection: () => Direction;
  /** Active deck — used to derive face labels for the placeholder so
   *  the user sees "Chercher pays / capitale" on capitales rather than
   *  the language-centric default. */
  getList: () => CardList;
  /** Jump the session to the given card — skips the normal pick-next
   *  flow and renders the card immediately. */
  goToCard: (card: Card) => void;
};

export type SearchController = {
  open: () => void;
  close: () => void;
};

const MAX_RESULTS = 50;

export function createSearch(deps: SearchDeps): SearchController {
  const modal = byId("searchModal");
  const input = byId<HTMLInputElement>("searchInput");
  const list = byId("searchResultsList");
  const closeBtn = byId<HTMLButtonElement>("searchModalCloseBtn");

  function renderEmpty(message: string): void {
    if (!list) return;
    list.innerHTML = `<div class="search-result-empty">${escapeHtml(message)}</div>`;
  }

  function renderResults(query: string): void {
    if (!list) return;
    const raw = query.trim();
    if (!raw) {
      renderEmpty(t("search_empty_hint") as string);
      return;
    }
    const needle = normalizeAnswer(raw);
    if (!needle) {
      renderEmpty(t("search_empty_hint") as string);
      return;
    }
    const cards = deps.getCards();
    const dir = deps.getDirection();
    // Match on the active direction's two faces only. The deck may carry
    // extra faces (e.g. English on a FR↔BG card) that the UI doesn't
    // render — matching against those would surface cards the user
    // can't locate visually.
    const matches: Card[] = [];
    for (const card of cards) {
      const from = card.faces[dir.from];
      const to = card.faces[dir.to];
      const hit =
        (from && normalizeAnswer(from).includes(needle)) ||
        (to && normalizeAnswer(to).includes(needle));
      if (hit) matches.push(card);
      if (matches.length >= MAX_RESULTS) break;
    }
    if (matches.length === 0) {
      renderEmpty(t("search_no_results") as string);
      return;
    }
    // Use the current direction for column order so the layout matches
    // what the user will see after clicking (from | to). Falls back to
    // any two available faces for decks with unusual face keys.
    list.innerHTML = matches
      .map((c) => {
        const fromText = c.faces[dir.from] || "";
        const toText = c.faces[dir.to] || "";
        const [left, right] = fromText && toText
          ? [fromText, toText]
          : Object.values(c.faces).filter(Boolean) as string[];
        return (
          `<div class="search-result-row" data-card-id="${escapeHtml(c.id)}">` +
          `<span class="search-result-prompt">${escapeHtml(left || "")}</span>` +
          `<span class="search-result-answer">${escapeHtml(right || "")}</span>` +
          `</div>`
        );
      })
      .join("");
    list.querySelectorAll<HTMLElement>(".search-result-row").forEach((row) => {
      row.addEventListener("click", () => {
        const id = row.dataset.cardId;
        if (!id) return;
        const card = cards.find((c) => c.id === id);
        if (!card) return;
        deps.goToCard(card);
        close();
      });
    });
  }

  function open(): void {
    if (!modal) return;
    show(modal);
    // Reset input + list on each open so stale results don't flash.
    // Placeholder adapts to the active deck's face labels: bilingual
    // shows "FR / BG" via LANG_SHORT, mono shows "pays / capitale" via
    // the face's i18n labelKey.
    if (input) {
      input.value = "";
      const list = deps.getList();
      const faceLabel = (idx: 0 | 1): string => {
        const face = list.faces[idx];
        return face.lang ? LANG_SHORT[face.lang] : (t(face.labelKey) as string);
      };
      input.placeholder = t(
        "search_placeholder",
        faceLabel(0),
        faceLabel(1),
      ) as string;
      // setTimeout gives the browser a tick to finish the show()
      // transition before focusing — avoids mobile keyboard jitter on
      // some browsers that race a layout shift against the focus.
      setTimeout(() => input.focus(), 30);
    }
    renderEmpty(t("search_empty_hint") as string);
  }

  function close(): void {
    hide(modal);
  }

  input?.addEventListener("input", () => {
    if (input) renderResults(input.value);
  });
  closeBtn?.addEventListener("click", () => close());
  modal?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "searchModal") close();
  });

  return { open, close };
}
